import { Component, Inject, OnInit } from '@angular/core';
import { ImageAssetsService } from '@services/assets/image-assets.service';
import { FileAssetsService } from '@services/assets/file-assets.service';
import { VideoAssetsService } from '@services/assets/video-assets.service';
import { MediaAssetsService } from '@services/assets/media-assets.service';
import { Models } from '@ibexfusion/models';
import { MessengerService } from '@services/messenger/messenger.service';
import { PageAction } from '../page-actions/page-actions.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { DialogService } from '@services/dialog/dialog.service';
import { TranslateService } from '@services/translate/translate.service';
import * as _ from 'lodash';
import { UploaderService } from '@services/uploader/uploader.service';
import { HttpClient } from '@angular/common/http';

@Component({
    selector: 'asset-selector',
    templateUrl: './asset-selector.component.html',
    styleUrls: ['./asset-selector.component.scss'],
    providers: [ImageAssetsService, FileAssetsService, VideoAssetsService, MediaAssetsService],
    standalone: false
})
export class AssetSelectorComponent implements OnInit {

  public pageActions: PageAction[];
  public systemMessages: any;
  public resourceTranslations: any;

  assets: Models.AssetBaseModel[] | any;
  selectedAssets: any = [];
  assetPath: string;
  allowedMimetypes: string;
  assetsService: any;
  options = { checkExists: true };

  isUploading;

  progress: any;

  title = 'Asset Manager';
  timestamp;

  skip = 0;
  limit = { 'image': 16, 'video': 4, 'file': 4, 'media': 16 };
  count: number;

  params: any;
  constructor(
    private imageAssetsService: ImageAssetsService, private fileAssetsService: FileAssetsService, private videoAssetsService: VideoAssetsService,
    private mediaAssetsService: MediaAssetsService, private ms: MessengerService, private snackbar: SnackbarService, private environmentService: EnvironmentService,
    public dialogRef: MatDialogRef<AssetSelectorComponent>, private dialogService: DialogService, private ts: TranslateService,
    private http: HttpClient, @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.params = { deleted: { $ne: true }, createdByImport: { $ne: true }, OwnerOrganisationID: this.ms.getOrganisation().OrganisationID, $sort: { created: -1 } };
  }

  async ngOnInit() {
    this.systemMessages = this.ts.get('system');
    this.resourceTranslations = this.ts.get('resources');
    this.timestamp = new Date().getTime();
    this.loadAssets();
  }

  async loadAssets() {
    if (this.data.type === 'image') {
      this.assetsService = this.imageAssetsService;
      this.assetPath = this.environmentService.getImageAssetPath();
      this.options['checkSize'] = !this.data.disableCheckSize;
      this.allowedMimetypes = this.data.type + '/*';
    } else if (this.data.type === 'file') {
      this.assetsService = this.fileAssetsService;
      this.assetPath = this.environmentService.getFileAssetPath();
      this.allowedMimetypes = 'application/pdf'
    } else if (this.data.type === 'video') {
      this.assetsService = this.videoAssetsService;
      this.assetPath = this.environmentService.getVideoAssetPath();
      this.allowedMimetypes = 'video/mp4' // todo allow other formats
    } else if (this.data.type === 'media') {
      this.assetsService = this.mediaAssetsService;
      this.assetPath = this.environmentService.getMediaAssetPath();
      this.allowedMimetypes = '*/*';
    } else {
      return;
    }
    const result = await this.assetsService.find(this.skip, this.limit[this.data.type], this.params);
    this.assets = result.data;
    this.count = result.total;
    this.isUploading = false;
  }

  async uploadAsset(event) {
    const src = event.target || event.srcElement;
    const element = event.path ? event.path[0] : event.target;
    if (!src.files.length) {
      return;
    }
    this.isUploading = true;
    if (this.data.type === 'video') {
      if (!this.allowedMimetypes.includes(src.files[0].type)) {
        element.value = null;
        this.isUploading = false;
        return this.loadAssets();
      }
      const uploader = new UploaderService(this.http, this.ms, { file: src.files[0], fileName: src.files[0].name });
      const alreadyExists = await uploader.checkUpload();
      uploader.onProgress(prog => {
        this.progress = prog.percentage;
      });
      uploader.onFinish(key => {
        return this.assetsService.create(this.makeAssetReady(key, src.files[0], true)).then(res => {
          element.value = null;
          this.isUploading = false;
          this.loadAssets();
        }).catch(err => {
          this.progress = null;
          this.isUploading = false
          this.loadAssets();
        });
      });
      if (alreadyExists) {
        const info = this.systemMessages.assets.alreadyExists;
        const confirm = this.systemMessages.assets.confirmExists
        const res = await this.dialogService.confirm(info + '<br/>' + confirm);
        if (!res) {
          return;
        }
        uploader.start();
      } else {
        uploader.start();
      }
    } else {
      for (const activeFile of src.files) {
        if (!activeFile.type.includes(this.allowedMimetypes.replace(/\*/gm, ''))) {
          continue;
        }
        const readerResult = await this.readAsDataURL(activeFile);
        await this.saveAsset(this.makeAssetReady(readerResult, activeFile));
      }
      element.value = null;
      this.isUploading = false;
      return this.loadAssets();
    }
  }

  async saveAsset(asset) {
    const result = await this.assetsService.create(asset).catch(err => {
      this.isUploading = false;
      try {
        let message = err.error.message || err.message;
        try {
          message = typeof message !== 'object' ? JSON.parse(message) : message;
        } catch (err) {
          this.snackbar.error('Could not upload and could not parse error: ' + message, 5000);
        }
        if (message.rejected) {
          let info;
          let confirm;
          let action;
          if (message.reason && message.reason === 'existing') {
            info = this.systemMessages.assets.alreadyExists;
            confirm = this.systemMessages.assets.confirmExists
            action = 'checkExists';
          }
          return this.dialogService.confirm(info + '<br/>' + confirm).then(res => {
            if (!res) {
              return;
            }
            asset.options[action] = false;
            this.isUploading = true;
            return this.saveAsset(asset);
          });
        }
      } catch (err) {
        const error = err && err.error ? err.error.message : err;
        this.snackbar.error('Could not upload: ' + error, 5000);
      }
    });
    this.assets.unshift(result);
    return true;
  }

  makeAssetReady(readerResult, file, video = false) {
    const asset = new Models.AssetBaseModel();

    if (video) {
      asset.data = { key: readerResult.split('assets/videos/').pop() };
      asset.path = readerResult.split('assets/videos/').pop();
    } else {
      asset.data = (typeof readerResult === 'string' ? readerResult : readerResult.toString());
    }
    asset.originalFileName = file.name;
    asset.mimeType = file.type;
    asset.fileSize = file.size;
    asset.extension = file.name.split('.').pop();
    asset.options = _.clone(this.options);
    return asset;
  }

  readAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onerror = reject;
      fr.onload = () => {
        resolve(fr.result);
      }
      fr.readAsDataURL(file);
    });
  }

  next(): void {
    this.skip += this.limit[this.data.type];
    this.loadAssets();
  }

  previous(): void {
    this.skip -= this.limit[this.data.type];
    this.loadAssets();
  }

  selectAsset(selectedAsset: any) {
    if (this.data.multiple) {
      if (selectedAsset['isSelected']) {
        selectedAsset['isSelected'] = false;
        const index = _.findIndex(this.selectedAssets, asset => {
          return asset['_id'] === selectedAsset._id;
        });
        if (index > -1) {
          this.selectedAssets.splice(index, 1);
        }
      } else {
        selectedAsset['isSelected'] = true;
        this.selectedAssets.push(selectedAsset);
      }
    } else {
      this.dialogRef.close(selectedAsset);
    }

  }

  insertAssets() {
    this.dialogRef.close(this.selectedAssets);
  }
}
